import { create } from 'zustand'
import { isUndefined, omitBy, clone } from 'lodash-es'
import type { ReactNode } from 'react'

export type Modal = {
  key?: string
  header?: ReactNode
  body: ReactNode
  open?: boolean
  form?: boolean
  closeConfirm?: boolean
  customActions?: any
  filePreview?: boolean
  closeOnDimmerClick?: boolean
  fullscreen?: boolean
  noPageHeader?: boolean
  onClose?: () => void
  replaceModal?: boolean
  isPage?: boolean
  saveButtonText?: string
  size?: 'fullscreen' | 'small' | 'mini' | 'tiny' | 'medium' | 'large' | 'full'
  submitDisabled?: boolean
  submitDisabledMessage?: string | null
  enableFixedButtons?: boolean
  topbarOffset?: boolean
}

interface ModalState {
  modals: Modal[]
  openModal: (variables: Modal) => void
  closeModal: () => void
  closeAllModals: () => void
  setSubmitDisabled: (disabled: boolean) => void
  setCloseConfirm: (confirm: boolean) => void
}

const defaultModal: Modal = {
  key: '',
  open: false,
  form: false,
  closeConfirm: false,
  customActions: null,
  filePreview: false,
  closeOnDimmerClick: false,
  fullscreen: true,
  noPageHeader: false,
  replaceModal: false,
  isPage: false,
  onClose: undefined,
  header: '',
  body: '',
  saveButtonText: '',
  size: 'small',
  submitDisabled: false,
  submitDisabledMessage: null,
  enableFixedButtons: false,
  topbarOffset: true,
}

const useModalStore = create<ModalState>(set => ({
  modals: [],
  openModal: variables =>
    set(state => {
      // Add key to prevent opening the same modal twice
      const key = JSON.stringify(variables)

      if (state.modals.some(modal => modal.key === key)) {
        return state
      }

      const newModal: Modal = {
        ...defaultModal,
        ...omitBy(variables, isUndefined),
        open: true,
        key,
      }

      if (variables.replaceModal) {
        return { modals: [newModal] }
      }

      return { modals: [...state.modals, newModal] }
    }),
  closeModal: () =>
    set(state => ({
      modals: state.modals.slice(0, state.modals.length - 1),
    })),
  closeAllModals: () =>
    set({
      modals: [],
    }),
  setSubmitDisabled: disabled =>
    set(state => {
      if (!state.modals.length) {
        return state
      }

      const lastModalIndex = state.modals.length - 1
      const newModals = state.modals.map(clone)
      newModals[lastModalIndex].submitDisabled = disabled
      return {
        modals: newModals,
      }
    }),
  setCloseConfirm: confirm =>
    set(state => {
      if (!state.modals.length) {
        return state
      }

      const lastModalIndex = state.modals.length - 1
      const newModals = state.modals.map(clone)
      newModals[lastModalIndex].closeConfirm = confirm
      return {
        modals: newModals,
      }
    }),
}))

export default useModalStore
